import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import {
  Send, Database, Sparkles, User, Bot,
  BookOpen, Clock, Image, ExternalLink, X, FileImage, ChevronDown, ChevronUp,
  Key, Hash
} from 'lucide-react';
import toast from 'react-hot-toast';
import { instancesApi, qaApi, promptsApi } from '../api';
import { Badge, Spinner } from '../components/UI';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ─── Page Image Viewer ────────────────────────────────────────────────────────
function PageImageModal({ src, pageNum, source, onClose }) {
  const { t } = useTranslation('qa');
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 10, padding: '8px 12px', background: 'var(--bg-2)',
          borderRadius: '8px 8px 0 0', border: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileImage size={14} style={{ color: 'var(--accent2)' }} />
            <span style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
              {source} — {t('page', { num: pageNum })}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a href={src} target="_blank" rel="noreferrer" style={{
              padding: '4px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)',
              borderRadius: 4, color: 'var(--accent2)', fontSize: 11, textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 4
            }}>
              <ExternalLink size={11} /> {t('view')}
            </a>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 4
            }}><X size={16} /></button>
          </div>
        </div>
        <img
          src={src}
          alt={t('page', { num: pageNum })}
          style={{
            maxWidth: '90vw', maxHeight: '82vh', objectFit: 'contain',
            display: 'block', border: '1px solid var(--border)',
            borderRadius: '0 0 8px 8px'
          }}
        />
      </div>
    </div>
  );
}

// ─── Source Card ──────────────────────────────────────────────────────────────
function SourceCard({ source, index, onImageClick }) {
  const { t } = useTranslation('qa');
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: 'var(--bg-3)', border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden', marginBottom: 8
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', cursor: 'pointer', gap: 10
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <span style={{
            width: 20, height: 20, borderRadius: 4, background: 'var(--accent-glow)',
            border: '1px solid var(--accent)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 10, color: 'var(--accent)', fontWeight: 700, flexShrink: 0
          }}>{index + 1}</span>
          <span style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📄 {source.source}
          </span>
          {source.pageNum && <Badge color="cyan">p.{source.pageNum}</Badge>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>
            {(source.score * 100).toFixed(0)}%
          </span>
          {source.imageUrl && (
            <button
              onClick={e => { e.stopPropagation(); onImageClick(source); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.4)',
                borderRadius: 4, color: 'var(--accent2)', fontSize: 10, cursor: 'pointer',
                fontFamily: 'var(--font-mono)', fontWeight: 600
              }}
            >
              <Image size={11} /> {t('view')}
            </button>
          )}
          {expanded ? <ChevronUp size={12} style={{ color: 'var(--text-3)' }} /> : <ChevronDown size={12} style={{ color: 'var(--text-3)' }} />}
        </div>
      </div>

      {expanded && (
        <div style={{
          padding: '8px 12px 12px', borderTop: '1px solid var(--border)',
          fontSize: 11, color: 'var(--text-3)', lineHeight: 1.7, fontFamily: 'var(--font-mono)'
        }}>
          {source.text}
        </div>
      )}

      {source.imageUrl && (
        <div
          onClick={() => onImageClick(source)}
          style={{
            borderTop: '1px solid var(--border)', cursor: 'pointer',
            overflow: 'hidden', maxHeight: 120, position: 'relative'
          }}
        >
          <img
            src={source.imageUrl}
            alt={t('page', { num: source.pageNum })}
            style={{ width: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block', opacity: 0.85 }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.7))',
            display: 'flex', alignItems: 'flex-end', padding: '8px 10px'
          }}>
            <span style={{ fontSize: 10, color: 'white', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Image size={10} /> {t('zoomHint')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Message ──────────────────────────────────────────────────────────────────
function Message({ msg, onImageClick }) {
  const { t } = useTranslation('qa');
  const isUser = msg.role === 'user';
  const [showSources, setShowSources] = useState(false);

  // Strip [Fonte N: ...] citations
  const cleanContent = (text) => (text || '')
    .replace(/\[Fonte \d+[^\]]*\]/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'flex-start',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 24, animation: 'fadeIn 0.2s ease'
    }}>
      {!isUser && (
        <div style={{
          width: 34, height: 34, borderRadius: 8, flexShrink: 0,
          background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 12px var(--accent-glow)'
        }}>
          <Bot size={16} color="white" />
        </div>
      )}

      <div style={{ maxWidth: '75%', minWidth: 60 }}>
        <div style={{
          background: isUser ? 'linear-gradient(135deg, var(--accent), var(--accent-dim))' : 'var(--bg-2)',
          border: `1px solid ${isUser ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: isUser ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
          padding: '12px 16px', color: 'var(--text-1)', fontSize: 13, lineHeight: 1.7
        }}>
          {isUser ? (
            <span>{msg.content}</span>
          ) : (
            <ReactMarkdown components={{
              p: ({ children }) => <p style={{ margin: '0 0 8px', lineHeight: 1.7 }}>{children}</p>,
              code: ({ children }) => <code style={{ background: 'var(--bg-3)', padding: '2px 5px', borderRadius: 4, fontSize: 12 }}>{children}</code>,
              pre: ({ children }) => <pre style={{ background: 'var(--bg-3)', padding: 12, borderRadius: 6, overflow: 'auto', marginTop: 8 }}>{children}</pre>,
              a: ({ href, children }) => {
                const isPageImage = href && (href.includes('/uploads/pages/') || href.match(/\.(png|jpg|jpeg)$/i));
                if (isPageImage) {
                  const pageMatch = String(children).match(/(\d+)/);
                  const pageNum = pageMatch ? pageMatch[1] : '?';
                  return (
                    <button
                      onClick={() => onImageClick({ imageUrl: href, pageNum, source: '' })}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', margin: '2px 4px',
                        background: 'rgba(6,182,212,0.15)',
                        border: '1px solid rgba(6,182,212,0.5)',
                        borderRadius: 6, color: 'var(--accent2)',
                        cursor: 'pointer', fontSize: 12,
                        fontFamily: 'var(--font-mono)', fontWeight: 600,
                        verticalAlign: 'middle'
                      }}
                    >
                      🖼️ {children}
                    </button>
                  );
                }
                return <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>{children}</a>;
              }
            }}>{cleanContent(msg.content)}</ReactMarkdown>
          )}
        </div>

        {!isUser && msg.sources && msg.sources.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <button
                onClick={() => setShowSources(s => !s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                  background: showSources ? 'var(--accent-glow)' : 'var(--bg-3)',
                  border: `1px solid ${showSources ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 4, color: showSources ? 'var(--accent)' : 'var(--text-3)',
                  cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)',
                  transition: 'all 0.15s'
                }}
              >
                📎 {msg.sources.length} {msg.sources.length === 1 ? t('sourceSingular') : t('sourcePlural')}
                {msg.sources.some(s => s.imageUrl) && (
                  <span style={{ marginLeft: 4, color: 'var(--accent2)' }}><Image size={11} /></span>
                )}
                {showSources ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              </button>
              {msg.latencyMs && (
                <span style={{ fontSize: 10, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Clock size={10} /> {msg.latencyMs}ms · {msg.model}
                </span>
              )}
            </div>
            {showSources && (
              <div style={{ marginTop: 4 }}>
                {msg.sources.map((s, i) => (
                  <SourceCard key={i} source={s} index={i} onImageClick={onImageClick} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {isUser && (
        <div style={{
          width: 34, height: 34, borderRadius: 8, flexShrink: 0,
          background: 'var(--bg-3)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <User size={16} style={{ color: 'var(--text-3)' }} />
        </div>
      )}
    </div>
  );
}

// ─── Main Q&A Page ────────────────────────────────────────────────────────────
export default function QA() {
  const { t } = useTranslation('qa');
  const { instanceId } = useParams();
  const [selectedInstanceId, setSelectedInstanceId] = useState(instanceId || '');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [topK, setTopK] = useState(5);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const bottomRef = useRef(null);

  const { data: instancesData } = useQuery({ queryKey: ['instances'], queryFn: instancesApi.list });
  const { data: promptsData }   = useQuery({ queryKey: ['prompts'],   queryFn: promptsApi.list });
  const { data: tenantsData }   = useQuery({
    queryKey: ['tenants'],
    queryFn: () => fetch(`${API_BASE}/tenants`, {
      credentials: 'include',
    }).then(r => r.json()),
  });

  const instances = instancesData?.instances || [];
  const prompts   = promptsData?.prompts     || [];
  const tenants   = tenantsData?.tenants     || [];

  const activeInstance = instances.find(i => i.id === selectedInstanceId);

  // Check if selected instance is protected
  const activeTenant = tenants.find(t => t.instanceId === selectedInstanceId);
  const isProtected  = activeTenant?.protected || false;

  // When instance changes, show API key field if protected
  useEffect(() => {
    if (isProtected) setShowApiKey(true);
    else { setShowApiKey(false); setApiKey(''); }
  }, [selectedInstanceId, isProtected]);

  useEffect(() => { if (instanceId) setSelectedInstanceId(instanceId); }, [instanceId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const queryMut = useMutation({
    mutationFn: ({ instanceId, payload }) => {
      const headers = {
        'Content-Type': 'application/json',
        ...(apiKey && { 'x-api-key': apiKey }),
      };
      return fetch(`${API_BASE}/qa/${instanceId}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      }).then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || t('errorStatus', { status: r.status }));
        return data;
      });
    },
    onSuccess: data => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.answer,
        sources: data.sources || [],
        latencyMs: data.latencyMs,
        model: data.model
      }]);
    },
    onError: err => {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ ' + t('errorPrefix', { message: err.message }), sources: [] }]);
      toast.error(err.message);
    }
  });

  const handleSubmit = () => {
    if (!question.trim()) return;
    if (!selectedInstanceId) return toast.error(t('instanceRequired'));
    if (isProtected && !apiKey.trim()) return toast.error(t('instanceKeyRequired'));
    const q = question.trim();
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setQuestion('');
    queryMut.mutate({
      instanceId: selectedInstanceId,
      payload: { question: q, promptId: selectedPromptId || undefined, topK }
    });
  };

  const handleImageClick = (source) => {
    setLightbox({ imageUrl: source.imageUrl, pageNum: source.pageNum, source: source.source });
  };

  return (
    <div style={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '12px 24px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-1)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={16} style={{ color: 'var(--accent)' }} />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 15 }}>{t('title')}</span>
        </div>

        {/* Instance */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Database size={13} style={{ color: 'var(--text-3)' }} />
          <select
            value={selectedInstanceId}
            onChange={e => setSelectedInstanceId(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('selectInstance')}</option>
            {instances.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>

        {/* Prompt */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BookOpen size={13} style={{ color: 'var(--text-3)' }} />
          <select
            value={selectedPromptId}
            onChange={e => setSelectedPromptId(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('selectPrompt')}</option>
            {prompts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {/* Top-K */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Hash size={13} style={{ color: 'var(--text-3)' }} />
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>{t('topK')}</span>
          <select
            value={topK}
            onChange={e => setTopK(Number(e.target.value))}
            style={{ ...selectStyle, minWidth: 60 }}
          >
            {[1, 2, 3, 5, 8, 10, 15, 20].map(k => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>

        {/* API Key — visible if instance is protected OR user clicks the key icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => setShowApiKey(s => !s)}
            title={t('apiKeyLabel')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
              color: isProtected ? 'var(--yellow)' : (showApiKey ? 'var(--accent)' : 'var(--text-3)'),
              display: 'flex', alignItems: 'center',
            }}
          >
            <Key size={14} />
          </button>
          {showApiKey && (
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={isProtected ? t('apiKeyRequired') : t('apiKeyPlaceholder')}
              style={{
                ...selectStyle,
                minWidth: 200,
                border: `1px solid ${isProtected && !apiKey ? 'var(--yellow)' : 'var(--border)'}`,
              }}
            />
          )}
          {isProtected && (
            <span style={{ fontSize: 10, color: 'var(--yellow)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
              🔒 {t('protected')}
            </span>
          )}
        </div>

        {messages.length > 0 && (
          <button
            onClick={() => setMessages([])}
            style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginLeft: 'auto' }}
          >
            <X size={13} /> {t('clearHistory')}
          </button>
        )}
      </div>

      {/* Instance info bar */}
      {activeInstance && (
        <div style={{ padding: '6px 24px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Badge color="blue">{activeInstance.name}</Badge>
          <Badge color="cyan">{activeInstance.qaProvider}/{activeInstance.qaModel}</Badge>
          <Badge>{activeInstance.stats?.documents || 0} docs</Badge>
          <Badge>top-{topK}</Badge>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: 'var(--bg-0)' }}>
        {messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'linear-gradient(135deg, var(--accent), var(--accent2))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, boxShadow: '0 0 30px var(--accent-glow)' }}>
              <Sparkles size={28} color="white" />
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, marginBottom: 10 }}>RAG Q&A Interface</div>
            <div style={{ color: 'var(--text-3)', fontSize: 13, maxWidth: 400, lineHeight: 1.7 }}>
              {selectedInstanceId
                ? t('placeholder')
                : t('selectInstance')}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <Message key={i} msg={msg} onImageClick={handleImageClick} />
            ))}
            {queryMut.isPending && (
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: 'linear-gradient(135deg, var(--accent), var(--accent2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bot size={16} color="white" />
                </div>
                <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '4px 14px 14px 14px', padding: '14px 18px', display: 'flex', gap: 8 }}>
                  {[0, 0.15, 0.3].map((delay, i) => (
                    <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block', animation: `pulse 1.2s ${delay}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', background: 'var(--bg-1)' }}>
        <div style={{ display: 'flex', gap: 12, background: 'var(--bg-2)', border: '1px solid var(--border-bright)', borderRadius: 12, padding: '8px 8px 8px 16px' }}>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
            placeholder={t('placeholder')}
            disabled={!selectedInstanceId || queryMut.isPending}
            rows={2}
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 13, resize: 'none', lineHeight: 1.6, paddingTop: 4 }}
          />
          <button
            onClick={handleSubmit}
            disabled={!question.trim() || !selectedInstanceId || queryMut.isPending}
            style={{
              width: 40, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', alignSelf: 'flex-end', flexShrink: 0,
              background: (question.trim() && selectedInstanceId) ? 'linear-gradient(135deg, var(--accent), var(--accent-dim))' : 'var(--bg-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
              boxShadow: question.trim() && selectedInstanceId ? '0 0 12px var(--accent-glow)' : 'none'
            }}
          >
            {queryMut.isPending ? <Spinner size={16} /> : <Send size={16} color={(question.trim() && selectedInstanceId) ? 'white' : 'var(--text-3)'} />}
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <PageImageModal
          src={lightbox.imageUrl}
          pageNum={lightbox.pageNum}
          source={lightbox.source}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

const selectStyle = {
  background: 'var(--bg-3)', border: '1px solid var(--border)',
  borderRadius: 6, color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
  fontSize: 12, padding: '5px 10px', outline: 'none', minWidth: 180,
};
